// Thin API client + data normalisation layer.
// Portfolio pages consume data through these helpers so they don't care
// whether it's coming from the backend, a fallback, or mock content.

import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BASE}/api`,
  withCredentials: true
});

export function fileUrl(name) {
  if (!name) return '';
  return `${BASE}/api/files/${encodeURIComponent(name)}`;
}

// Convert a backend MediaItem into the shape the existing pages/cards expect.
export const modelStyle = (raw) => ({
  shading: raw.shading || 'normal',
  lighting: raw.lighting || 'balanced',
  lights: Array.isArray(raw.lights) ? raw.lights : null,
  brightness: raw.brightness ?? 1,
  outline: { on: !!raw.outline, width: raw.outline_width ?? 2, color: raw.outline_color || '#000000' }
});

export function normalizeItem(raw) {
  const base = {
    id: raw.id,
    title: raw.title,
    date: raw.date,
    description: raw.description || '',
    type: raw.type,
    category: raw.category,
    order: raw.order ?? 0,
    _remote: true
  };
  if (raw.type === 'model') {
    return {
      ...base,
      modelUrl: fileUrl(raw.filename),
      expressions: raw.expressions || [],
      looks: raw.looks || null,
      textures: textureMaps(raw.textures),
      exprNav: raw.expr_nav !== false,
      ...modelStyle(raw),
      color: '#f5a623' // default highlight for server-loaded models
    };
  }
  return {
    ...base,
    src: fileUrl(raw.filename),
    poster: raw.poster_filename ? fileUrl(raw.poster_filename) : raw.type === 'video' ? null : fileUrl(raw.filename)
  };
}

export async function fetchMedia(category) {
  const { data } = await api.get('/media', { params: category ? { category } : {} });
  return data.map(normalizeItem);
}

// ---------- Admin ----------
export async function uploadMedia({ file, poster, category, title, date, description }) {
  const fd = new FormData();
  fd.append('file', file);
  if (poster) fd.append('poster', poster);
  fd.append('category', category);
  fd.append('title', title);
  fd.append('date', date);
  fd.append('description', description || '');
  const { data } = await api.post('/admin/media', fd, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
}

export async function deleteMedia(id) {
  const { data } = await api.delete(`/admin/media/${id}`);
  return data;
}

export async function updateMedia(id, patch) {
  const { data } = await api.patch(`/admin/media/${id}`, patch);
  return data;
}

export async function reorderMedia(category, ids) {
  const { data } = await api.put('/admin/media/order', { category, ids });
  return data;
}

export async function saveExpressions(id, expressions) {
  const { data } = await api.put(`/admin/media/${id}/expressions`, { expressions });
  return data;
}

export async function saveLooks(id, looks) {
  const { data } = await api.put(`/admin/media/${id}/looks`, looks);
  return data;
}

export async function saveDisplay(id, patch) {
  const { data } = await api.put(`/admin/media/${id}/display`, patch);
  return data;
}

export async function replaceModelFile(id, file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.put(`/admin/media/${id}/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 });
  return data;
}

export async function syncCheck(previewUrl) {
  const { data } = await api.post('/admin/sync/check', { preview_url: previewUrl || '' }, { timeout: 90000 });
  return data;
}

export async function listStylePresets() {
  const { data } = await api.get('/admin/style-presets');
  return data;
}

export async function createStylePreset(name, style) {
  const { expr_nav: _drop, ...rest } = style;
  const { data } = await api.post('/admin/style-presets', { name, style: rest });
  return data;
}

export async function deleteStylePreset(id) {
  await api.delete(`/admin/style-presets/${id}`);
}

export async function uploadLookTexture(id, file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(`/admin/media/${id}/look-texture`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data.filename;
}

export async function uploadTextures(id, files) {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  const { data } = await api.post(`/admin/media/${id}/textures`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}

export async function deleteTexture(id, material) {
  const { data } = await api.delete(`/admin/media/${id}/textures/${encodeURIComponent(material)}`);
  return data;
}

export const textureMaps = (textures) => (textures || []).map((t) => ({ material: t.material, url: fileUrl(t.filename) }));

// ---------- Footer contact links ----------
export async function fetchContact() {
  const { data } = await api.get('/contact');
  return data;
}

export async function updateContact(payload) {
  const { data } = await api.put('/admin/contact', payload);
  return data;
}

export async function uploadPortfolio(file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/admin/portfolio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}

export async function removePortfolio() {
  const { data } = await api.delete('/admin/portfolio');
  return data;
}

// ---------- Preview -> live sync ----------
export async function syncSource() {
  const { data } = await api.get('/admin/sync/source');
  return data.preview_url;
}

export async function syncPlan(previewUrl) {
  const { data } = await api.post('/admin/sync/plan', { preview_url: previewUrl || '' }, { timeout: 90000 });
  return data;
}

export async function syncApply(jobId) {
  const { data } = await api.post('/admin/sync/apply', { job_id: jobId }, { timeout: 120000 });
  return data;
}

export async function syncLast() {
  const { data } = await api.get('/admin/sync/last');
  return data;
}

export async function syncVerify(jobId) {
  const { data } = await api.post('/admin/sync/verify', { job_id: jobId }, { timeout: 120000 });
  return data;
}

export async function syncFile(jobId, filename) {
  const { data } = await api.post('/admin/sync/file', { job_id: jobId, filename }, { timeout: 900000 });
  return data;
}

// ---------- Categories (channels) ----------
export async function fetchCategories() {
  const { data } = await api.get('/categories');
  return data;
}

export async function createCategory(payload) {
  const { data } = await api.post('/admin/categories', payload);
  return data;
}

export async function updateCategory(key, patch) {
  const { data } = await api.patch(`/admin/categories/${key}`, patch);
  return data;
}

export async function deleteCategory(key) {
  const { data } = await api.delete(`/admin/categories/${key}`);
  return data;
}

export async function reorderCategories(keys) {
  const { data } = await api.put('/admin/categories/order', { keys });
  return data;
}

// ---------- Auth ----------
export async function exchangeSession(session_id) {
  const { data } = await api.post('/auth/session', { session_id });
  return data.user;
}

export async function fetchMe() {
  const { data } = await api.get('/auth/me');
  return data;
}

export async function logout() {
  await api.post('/auth/logout');
}

export function googleLoginUrl(redirectPath = '/admin') {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirect = window.location.origin + redirectPath;
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}
